import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Play, Ruler, Layers, Fingerprint, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { heroMedia } from "@/content/site";
import { MaskLines, Arrow, Label, RotatingBadge } from "@/components/apex/primitives";

const STRIP_ICONS = [Ruler, Layers, Fingerprint];

const Hero = () => {
  const { t, isRtl } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  /* على الهاتف نحمّل نسخة 480p خفيفة حتى يبدأ التشغيل التلقائي فورًا */
  const useMobileVideo =
    !!heroMedia.videoMobile && typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    /* iOS/Android: يجب أن تكون خاصية muted موجودة كسِمة فعلية قبل التشغيل التلقائي
       (React لا يعكس muted كسِمة DOM) */
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    const tryPlay = () => el.play().catch(() => {});
    tryPlay();
    el.addEventListener("loadeddata", tryPlay, { once: true });
    el.addEventListener("canplay", tryPlay, { once: true });
    /* احتياط لوضع توفير الطاقة: أول لمسة/تمرير يشغّل الفيديو */
    const gestures = ["touchstart", "pointerdown", "scroll", "keydown"];
    const onGesture = () => {
      if (el.paused) tryPlay();
      if (!el.paused) gestures.forEach((g) => window.removeEventListener(g, onGesture));
    };
    gestures.forEach((g) => window.addEventListener(g, onGesture, { passive: true }));
    const onVis = () => document.visibilityState === "visible" && el.paused && tryPlay();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
      gestures.forEach((g) => window.removeEventListener(g, onGesture));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (el.paused) el.play().catch(() => {});
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-[var(--charcoal)] pt-28 text-white sm:pt-36"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        {heroMedia.video ? (
          <video
            ref={videoRef}
            poster={heroMedia.image}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label={isRtl ? heroMedia.alt_ar : heroMedia.alt_en}
            data-testid="hero-video"
            className="h-full w-full object-cover"
            style={{ objectPosition: "center 35%" }}
          >
            {useMobileVideo ? (
              <source src={heroMedia.videoMobile} type="video/mp4" />
            ) : (
              <>
                <source src={heroMedia.video} type="video/mp4" />
                {heroMedia.videoWebm && <source src={heroMedia.videoWebm} type="video/webm" />}
              </>
            )}
          </video>
        ) : (
          <img
            src={heroMedia.image}
            alt={isRtl ? heroMedia.alt_ar : heroMedia.alt_en}
            width="1500"
            height="2000"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
            style={{ objectPosition: "center 40%" }}
          />
        )}
      </motion.div>
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(to bottom, rgba(21,21,21,0.55) 0%, rgba(21,21,21,0.35) 40%, rgba(21,21,21,0.6) 75%, rgba(21,21,21,0.92) 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto flex w-full max-w-[1400px] flex-1 flex-col justify-center px-5 py-14 sm:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <Label dark>{t.hero.eyebrow}</Label>
            </motion.div>

            <h1 className="ap-display mt-6 text-[2.7rem] text-white sm:text-[3.6rem] lg:text-[4.6rem]">
              <MaskLines lines={t.hero.title} delay={0.15} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-6 max-w-xl text-[15px] leading-[1.9] text-white/80 sm:text-base"
            >
              {t.hero.body}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-5"
            >
              <a href="#collections" data-testid="hero-primary-cta" className="ap-btn">
                {t.hero.primary}
                <Arrow />
              </a>
              <a href="#film" data-testid="hero-secondary-cta" className="group inline-flex items-center gap-3 font-semibold text-white">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--yellow)] text-[var(--charcoal)] transition-transform duration-500 group-hover:scale-110">
                  <Play className="h-4 w-4 translate-x-[1px] fill-current rtl:-translate-x-[1px] rtl:scale-x-[-1]" />
                </span>
                {t.hero.secondary}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block lg:pe-10"
          >
            <RotatingBadge text={t.nav.contact} href="#contact" testid="hero-rotating-badge" />
          </motion.div>
        </div>
      </div>

      {heroMedia.video && (
        <button
          type="button"
          onClick={toggleSound}
          aria-pressed={!muted}
          aria-label={muted ? t.hero.unmute : t.hero.mute}
          data-testid="hero-sound-toggle"
          className="ap-icon-ghost absolute end-5 top-[6.5rem] z-10 h-12 w-12 sm:end-8 sm:top-auto sm:bottom-[8.5rem]"
        >
          {muted ? <VolumeX className="h-5 w-5" strokeWidth={1.8} /> : <Volume2 className="h-5 w-5" strokeWidth={1.8} />}
        </button>
      )}

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.05, duration: 0.9 }}
        className="relative mx-auto grid w-full max-w-[1400px] gap-6 border-t border-white/15 px-5 py-9 sm:grid-cols-3 sm:px-8"
        data-testid="hero-strip"
      >
        {t.hero.strip.map((s, i) => {
          const Icon = STRIP_ICONS[i];
          return (
            <li key={s} className="group flex items-center gap-4">
              <span className="ap-icon h-12 w-12">
                <Icon className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <span className="text-[15px] font-semibold text-white">{s}</span>
            </li>
          );
        })}
      </motion.ul>
    </section>
  );
};

export default Hero;
